import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  ClipboardCheck,
  ListChecks,
  Clock,
  Users2,
  Banknote,
  Share2,
  Layers,
  Settings as SettingsIcon,
  Sparkles,
  ArrowLeft,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { getAdminToken, setAdminToken, clearAdminToken, adminGetDashboard } from "@/lib/afritask";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/afriselection", label: "AfriSelection AI", icon: Sparkles },
  { to: "/admin/tasks", label: "Tasks", icon: ListChecks },
  { to: "/admin/reviews", label: "Legacy reviews", icon: ClipboardCheck },
  { to: "/admin/users", label: "Users", icon: Users2 },
  { to: "/admin/waitlist", label: "Waitlist", icon: Clock },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: Banknote },
  { to: "/admin/referrals", label: "Referrals", icon: Share2 },
  { to: "/admin/selection-logs", label: "Selection logs", icon: Layers },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

const TITLES = {
  "/admin/dashboard": "Admin dashboard",
  "/admin/afriselection": "AfriSelection AI",
  "/admin/reviews": "Submission review queue",
  "/admin/tasks": "Tasks",
  "/admin/users": "Users",
  "/admin/waitlist": "Waitlist",
  "/admin/withdrawals": "Withdrawals",
  "/admin/referrals": "Referrals",
  "/admin/selection-logs": "Selection logs",
  "/admin/settings": "Platform settings",
};

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [token, setToken] = useState("");

  useEffect(() => {
    const t = getAdminToken();
    if (!t) {
      setChecking(false);
      return;
    }
    adminGetDashboard()
      .then(() => setAuthed(true))
      .catch(() => {
        clearAdminToken();
        setAuthed(false);
      })
      .finally(() => setChecking(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!token) return;
    setAdminToken(token.trim());
    try {
      await adminGetDashboard();
      setAuthed(true);
      // Silent — the admin dashboard rendering IS the confirmation.
    } catch {
      clearAdminToken();
      toast.error("Invalid admin token");
    }
  };

  const logout = () => {
    clearAdminToken();
    setAuthed(false);
    navigate("/admin");
  };

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-neutral-500">
        Verifying admin session…
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="grid min-h-screen place-items-center bg-neutral-50 p-6">
        {/* Global <AppToaster/> is mounted in App.js */}
        <Card className="w-full max-w-md">
          <CardContent className="space-y-4 p-8">
            <div className="mb-2 flex items-center justify-between">
              <Logo />
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800">
                Admin
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Admin sign in</h1>
              <p className="mt-1 text-sm text-neutral-500">
                Enter the admin token to access the review queue.
              </p>
            </div>
            <form onSubmit={handleLogin} className="space-y-3">
              <Input
                type="password"
                placeholder="X-Admin-Token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                data-testid="admin-token-input"
              />
              <Button
                type="submit"
                className="w-full bg-amber-700 hover:bg-amber-800"
                data-testid="admin-login-btn"
              >
                Sign in
              </Button>
            </form>
            <button
              onClick={() => navigate("/")}
              className="text-xs font-medium text-neutral-500 hover:text-neutral-700"
            >
              ← Back to site
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const title = TITLES[location.pathname] || "Admin";

  return (
    <div className="flex min-h-screen bg-neutral-50">
      {/* Global <AppToaster/> is mounted in App.js */}
      <aside className="hidden w-60 shrink-0 border-r border-neutral-200 bg-neutral-900 text-neutral-200 lg:block">
        <div className="flex h-16 items-center border-b border-neutral-800 px-5">
          <Logo variant="light" />
          <span className="ml-2 rounded-full bg-red-500/20 px-2 py-0.5 text-[9px] font-semibold text-red-300">
            ADMIN
          </span>
        </div>
        <nav className="flex flex-col gap-0.5 px-2 py-4">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`admin-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
                  isActive
                    ? "bg-amber-700 text-white"
                    : "text-neutral-400 hover:bg-neutral-800 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 border-t border-neutral-800 px-3 py-4 space-y-1">
          <button
            onClick={() => navigate("/app/dashboard")}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium text-neutral-400 transition hover:bg-neutral-800 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to app
          </button>
          <button
            onClick={logout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-xs font-medium text-neutral-400 transition hover:bg-neutral-800 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-neutral-200 bg-white px-6">
          <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        </header>
        <main className="min-w-0 flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
